import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "users-column"
}
const _hoisted_2 = { class: "users-header" }
const _hoisted_3 = { class: "user-card" }
const _hoisted_4 = { class: "user-info" }
const _hoisted_5 = { class: "user-name" }
const _hoisted_6 = {
  key: 0,
  class: "user-role"
}
const _hoisted_7 = { class: "user-capacity" }
const _hoisted_8 = { class: "capacity-label" }
const _hoisted_9 = { class: "capacity-hours" }
const _hoisted_10 = { class: "user-weekly-summary" }
const _hoisted_11 = { class: "weekly-logged" }
const _hoisted_12 = { class: "schedule-grid-wrapper" }
const _hoisted_13 = { class: "schedule-header" }
const _hoisted_14 = { class: "week-header-row" }
const _hoisted_15 = { class: "day-header-row" }
const _hoisted_16 = { class: "day-name" }
const _hoisted_17 = { class: "day-date" }
const _hoisted_18 = { class: "month-background-cells" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = {
  key: 0,
  class: "cell-timesheet-total"
}
const _hoisted_21 = { class: "monthly-items-layer" }
const _hoisted_22 = ["title", "onClick"]
const _hoisted_23 = ["onMousedown"]
const _hoisted_24 = { class: "planning-item-content" }
const _hoisted_25 = { class: "planning-item-label" }
const _hoisted_26 = ["onMousedown"]
const _hoisted_27 = ["onClick"]
const _hoisted_28 = { class: "cell-items" }
const _hoisted_29 = ["title", "onClick"]
const _hoisted_30 = { class: "planning-item-content" }
const _hoisted_31 = { class: "planning-item-label" }
const _hoisted_32 = { class: "planning-item-duration" }
const _hoisted_33 = ["title", "onClick"]
const _hoisted_34 = ["onMousedown"]
const _hoisted_35 = { class: "planning-item-label" }
const _hoisted_36 = { class: "planning-item-duration" }
const _hoisted_37 = ["onMousedown"]
const _hoisted_38 = ["title"]
const _hoisted_39 = { class: "planning-item-content" }
const _hoisted_40 = { class: "planning-item-label" }
const _hoisted_41 = ["title", "onClick"]
const _hoisted_42 = { class: "planning-item-content" }
const _hoisted_43 = { class: "planning-item-label" }
const _hoisted_44 = { class: "planning-item-duration" }
const _hoisted_45 = ["title", "onClick"]
const _hoisted_46 = { class: "planning-item-content" }
const _hoisted_47 = { class: "planning-item-label" }
const _hoisted_48 = { class: "planning-item-duration" }
const _hoisted_49 = ["title"]
const _hoisted_50 = { class: "planning-item-content" }
const _hoisted_51 = { class: "planning-item-label" }
const _hoisted_52 = { class: "planning-item-duration" }
const _hoisted_53 = {
  key: 0,
  class: "cell-timesheet-total"
}

import type {
  PlanningPlannedTime,
  PlanningTimeOff,
  PlanningTimesheetEntry,
  TimesheetSuggestion,
} from "@/helpers/planning/planningDataNormalizers";
import type {
  PlanningProject,
  PlanningUser,
  PlanningHoliday,
  VisibleDay,
} from "./type";
import { AoAvatar } from "@26lights/orcha";
import { Icon } from "@iconify/vue";
import { isToday } from "@/helpers/planning/planningDates";
import { isDateInHoliday } from "@/helpers/planning/planningRecurrence";
import {
  getProjectColor,
  getProjectName,
  getUserCapacity,
  getUserColor,
  getUserInitials,
  getUserName,
  hasUserAvatar,
} from "@/helpers/planning/planningNormalizers";
import { computed, ref, toRefs } from "vue";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useI18n } from "vue-i18n";
import enUK from "@/locales/en-UK.json";

type MessageSchema = typeof enUK;

type WeekGroup = {
  label: string;
  startIndex: number;
  dayCount: number;
};

type MonthlyPlanningItemKind =
  | "planned_time"
  | "time_off"
  | "holiday"
  | "timesheet"
  | "suggestion"
  | "nonworking";

type MonthlyPlanningItem = {
  key: string;
  kind: MonthlyPlanningItemKind;
  startIndex: number;
  endIndex: number;
  lane: number;
  plannedTime?: PlanningPlannedTime;
  timeOff?: PlanningTimeOff;
  holiday?: PlanningHoliday;
  timesheet?: PlanningTimesheetEntry;
  suggestion?: TimesheetSuggestion;
  user?: PlanningUser;
  day?: VisibleDay;
};

const MIN_ROW_HEIGHT = 100;
const MIN_ITEM_HEIGHT = 24;
const CELL_VERTICAL_PADDING = 8;
const CELL_ITEM_GAP = 2;
const CELL_CLICK_SPACE_HEIGHT = 16;

type ResizeEdge = "left" | "right";
type DragState = {
  plannedTime: PlanningPlannedTime;
  edge: ResizeEdge;
  startX: number;
  originalStartDate: string;
  originalEndDate: string;
  currentStartDate: string;
  currentEndDate: string;
} | null;


export default /*@__PURE__*/_defineComponent({
  __name: 'PlanningCalendar',
  props: {
    users: {},
    visibleDays: {},
    plannedTimes: {},
    timeOffs: {},
    holidays: {},
    projects: {},
    timesheetEntries: {},
    showLoggedTime: { type: Boolean, default: true },
    showPlannedTime: { type: Boolean, default: true },
    showLoggedTimeSuggestions: { type: Boolean, default: true },
    readonly: { type: Boolean, default: false },
    showUserColumn: { type: Boolean, default: true },
    minRowHeight: { default: MIN_ROW_HEIGHT },
    fillAvailableHeight: { type: Boolean, default: false },
    highlightToday: { type: Boolean, default: true },
    currentUserId: { default: null },
    isAdmin: { type: Boolean, default: false },
    monthView: { type: Boolean, default: false }
  },
  emits: ["open-new-planned-time", "open-new-time-off", "open-edit-planned-time", "open-edit-time-off", "open-new-timesheet", "open-edit-timesheet", "open-new-timesheet-suggestion", "resize-planned-time"],
  setup(__props: any, { emit: __emit }) {

dayjs.extend(isoWeek);

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
});

const props = __props;

const {
  users,
  visibleDays,
  readonly,
  timesheetEntries,
  showLoggedTime,
  showPlannedTime,
  showLoggedTimeSuggestions,
  currentUserId,
  isAdmin,
  monthView,
} = toRefs(props);

// Check if the current user can interact with a cell/item belonging to another user
const canInteractWithUser = (targetUserId: string): boolean => {
  // Admins can interact with any user's cells
  if (isAdmin.value) {
    return true;
  }
  // Non-admin users can only interact with their own cells
  return currentUserId.value === targetUserId;
};

const emit = __emit;

const usersListRef = ref<HTMLElement | null>(null);
const scheduleBodyRef = ref<HTMLElement | null>(null);

// Drag-to-resize state
const dragState = ref<DragState>(null);

// Check if a planned time can be resized
const canResizePlannedTime = (plannedTime: PlanningPlannedTime): boolean => {
  if (props.readonly) return false;
  if (!canInteractWithUser(plannedTime.user.id)) return false;
  // Only allow resize for non-recurring planned times
  if (plannedTime.frequency && plannedTime.frequency !== "no_repeat") {
    return false;
  }
  return true;
};

// Get the date from a day index
const getDateFromDayIndex = (index: number): string | null => {
  const day = visibleDays.value[index];
  return day ? day.date : null;
};

// Calculate the new date based on mouse position
const calculateNewDateFromMouseX = (mouseX: number): string | null => {
  const scheduleBody = scheduleBodyRef.value;
  if (!scheduleBody) return null;

  const cells = scheduleBody.querySelectorAll(
    ".schedule-row:first-child .schedule-cell",
  );
  if (cells.length === 0) return null;

  // Find which cell the mouse is over
  let targetIndex = -1;
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i] as HTMLElement;
    const rect = cell.getBoundingClientRect();
    if (mouseX >= rect.left && mouseX <= rect.right) {
      targetIndex = i;
      break;
    }
  }

  if (targetIndex === -1) return null;
  return getDateFromDayIndex(targetIndex);
};

// Handle drag start
const handleResizeStart = (
  event: MouseEvent,
  plannedTime: PlanningPlannedTime,
  edge: ResizeEdge,
) => {
  if (!canResizePlannedTime(plannedTime)) return;

  event.preventDefault();
  event.stopPropagation();

  dragState.value = {
    plannedTime,
    edge,
    startX: event.clientX,
    originalStartDate: plannedTime.startDate,
    originalEndDate: plannedTime.endDate,
    currentStartDate: plannedTime.startDate,
    currentEndDate: plannedTime.endDate,
  };

  // Add global event listeners
  document.addEventListener("mousemove", handleResizeMove);
  document.addEventListener("mouseup", handleResizeEnd);
};

// Handle drag move
const handleResizeMove = (event: MouseEvent) => {
  if (!dragState.value) return;

  const newDate = calculateNewDateFromMouseX(event.clientX);

  if (!newDate) return;

  const { edge } = dragState.value;

  if (edge === "left") {
    dragState.value.currentStartDate = newDate;
  } else {
    dragState.value.currentEndDate = newDate;
  }
};

// Handle drag end
const handleResizeEnd = () => {
  if (!dragState.value) return;

  const {
    plannedTime,
    edge,
    currentStartDate,
    currentEndDate,
    originalStartDate,
    originalEndDate,
  } = dragState.value;

  // Only emit if dates actually changed
  if (
    currentStartDate !== originalStartDate ||
    currentEndDate !== originalEndDate
  ) {
    emit(
      "resize-planned-time",
      plannedTime,
      currentStartDate,
      currentEndDate,
      edge,
    );
  }

  dragState.value = null;
  document.removeEventListener("mousemove", handleResizeMove);
  document.removeEventListener("mouseup", handleResizeEnd);
};

// Get the display dates for a planned time (considering drag state)
const getPlannedTimeDisplayDates = (plannedTime: PlanningPlannedTime) => {
  if (dragState.value && dragState.value.plannedTime.id === plannedTime.id) {
    return {
      startDate: dragState.value.currentStartDate,
      endDate: dragState.value.currentEndDate,
    };
  }
  return {
    startDate: plannedTime.startDate,
    endDate: plannedTime.endDate,
  };
};

// Check if this is the start day of a planned time (for drag handle positioning)
const isPlannedTimeStartDay = (
  date: string,
  plannedTime: PlanningPlannedTime,
): boolean => {
  const { startDate } = getPlannedTimeDisplayDates(plannedTime);
  return date === startDate;
};

// Check if this is the end day of a planned time (for drag handle positioning)
const isPlannedTimeEndDay = (
  date: string,
  plannedTime: PlanningPlannedTime,
): boolean => {
  const { endDate } = getPlannedTimeDisplayDates(plannedTime);
  return date === endDate;
};

const weekGroups = computed<WeekGroup[]>(() => {
  const groups: WeekGroup[] = [];

  visibleDays.value.forEach((day, index) => {
    const previousGroup = groups[groups.length - 1];

    if (!previousGroup || isWeekStart(index)) {
      groups.push({
        label: getWeekGroupLabel(index),
        startIndex: index,
        dayCount: 1,
      });

      return;
    }

    previousGroup.dayCount++;
    previousGroup.label = getWeekGroupLabel(previousGroup.startIndex);
  });

  return groups;
});

const firstVisibleWeekDays = computed(() => {
  const firstWeekGroup = weekGroups.value[0];

  if (!firstWeekGroup) {
    return [];
  }

  return visibleDays.value.slice(
    firstWeekGroup.startIndex,
    firstWeekGroup.startIndex + firstWeekGroup.dayCount,
  );
});

const getWeekGroupLabel = (startIndex: number) => {
  const weekDays = visibleDays.value.slice(startIndex, startIndex + 5);
  const monthLabels = [
    ...new Set(
      weekDays.map((day) => {
        return day.monthLabel;
      }),
    ),
  ];

  if (!props.showUserColumn) {
    return monthLabels.join(" - ");
  }

  const firstDay = weekDays[0];
  const weekNumber = dayjs(firstDay.date).isoWeek();

  return `Week ${weekNumber}, ${monthLabels.join(" - ")}`;
};

const isWeekStart = (index: number) => {
  if (index === 0) {
    return false;
  }

  return visibleDays.value[index]?.dayOfWeek === 1;
};

const handleUsersListScroll = () => {
  if (scheduleBodyRef.value && usersListRef.value) {
    scheduleBodyRef.value.scrollTop = usersListRef.value.scrollTop;
  }
};

const handleScheduleBodyScroll = () => {
  if (usersListRef.value && scheduleBodyRef.value) {
    usersListRef.value.scrollTop = scheduleBodyRef.value.scrollTop;
  }
};

const getPlannedTimesForCell = (userId: string, date: string) => {
  if (!shouldShowPlannedTime(date)) {
    return [];
  }

  // Hide planned times on holidays
  const holidays = getHolidaysForCell(userId, date);
  if (holidays.length > 0) {
    return [];
  }

  // Show ALL planned times, even if they overlap with time off
  // The validation should prevent overlaps at creation time
  return getAllPlannedTimesForCell(userId, date);
};

const getAllPlannedTimesForCell = (userId: string, date: string) => {
  return props.plannedTimes.filter((plannedTime) => {
    return plannedTime.user?.id === userId && plannedTime.startDate === date;
  });
};

const getTimeOffsForCell = (userId: string, date: string) => {
  return props.timeOffs.filter((timeOff) => {
    return timeOff.user?.id === userId && timeOff.startDate === date;
  });
};

const getHolidaysForCell = (userId: string, date: string) => {
  const userHolidayEntry = props.holidays.find((h) => h.user.id === userId);

  if (!userHolidayEntry) {
    return [];
  }

  return userHolidayEntry.holidays.filter((holiday) => {
    return isDateInHoliday(date, holiday);
  });
};

const getTimesheetEntriesForCell = (userId: string, date: string) => {
  if (!shouldShowLoggedTime(date)) {
    return [];
  }

  return timesheetEntries.value.filter((entry) => {
    return entry.user?.id === userId && entry.date === date;
  });
};

// Calculate total duration for a cell (day)
// Includes: timesheet logs + paid leave + sick days
const getTotalTimesheetDurationForCell = (
  userId: string,
  date: string,
): number => {
  let totalMinutes = 0;

  // Add timesheet logs
  totalMinutes += getTimesheetEntriesForCell(userId, date).reduce(
    (total, entry) => {
      return total + entry.duration;
    },
    0,
  );

  // Add paid leave and sick days
  const timeOffs = getTimeOffsForCell(userId, date);
  for (const timeOff of timeOffs) {
    const type = timeOff.type ?? timeOff.timeOffType ?? "";
    if (type === "paid_leave" || type === "sick_leave") {
      totalMinutes += timeOff.durationPerDay ?? 0;
    }
  }

  return totalMinutes;
};

// Calculate total logged minutes for the first visible week
// Includes: timesheet logs + paid leave + sick days (via getTotalTimesheetDurationForCell)
const getWeeklyLoggedMinutes = (user: PlanningUser): number => {
  return firstVisibleWeekDays.value.reduce((total, day) => {
    return total + getTotalTimesheetDurationForCell(user.id, day.date);
  }, 0);
};

// Check if a time-off is a full day (8h = 480 minutes)
const isFullDayTimeOff = (timeOff: PlanningTimeOff): boolean => {
  const durationPerDay = timeOff.durationPerDay ?? 0;
  return durationPerDay >= 480;
};

// Check if cell is missing timesheet (for past dates only, up to yesterday)
// Returns true if:
// - Date is before today (not including today)
// - No timesheet entries
// - No full-day time-off (8h)
// - No holiday
// - No full-day non-working slot (8h)
const isCellMissingTimesheet = (
  user: PlanningUser,
  day: VisibleDay,
): boolean => {
  const userId = user.id;
  const date = day.date;

  // Only check dates before today (not today or future)
  const today = new Date().toISOString().split("T")[0];
  if (date >= today) {
    return false;
  }

  // Check if there are timesheet entries
  const timesheets = getTimesheetEntriesForCell(userId, date);
  if (timesheets.length > 0) {
    return false;
  }

  // Check if there's a full-day time-off (8h)
  const timeOffs = getTimeOffsForCell(userId, date);
  const hasFullDayTimeOff = timeOffs.some(isFullDayTimeOff);
  if (hasFullDayTimeOff) {
    return false;
  }

  // Check if there's a holiday
  const holidays = getHolidaysForCell(userId, date);
  if (holidays.length > 0) {
    return false;
  }

  // Check if there's a full-day non-working slot (8h)
  const nonWorkingHours = getNonWorkingHours(user, day);
  return nonWorkingHours < 8;
};

const getLoggedHoursForPlannedTime = (
  userId: string,
  date: string,
  plannedTime: PlanningPlannedTime,
) => {
  return timesheetEntries.value
    .filter((entry) => {
      return (
        entry.user?.id === userId &&
        entry.date === date &&
        entry.project?.id === plannedTime.project?.id
      );
    })
    .reduce((total, entry) => {
      return total + entry.duration / 60;
    }, 0);
};

const getTimesheetSuggestionsForCell = (
  user: PlanningUser,
  day: VisibleDay,
): TimesheetSuggestion[] => {
  if (!shouldShowLoggedTime(day.date)) {
    return [];
  }

  // If showLoggedTimeSuggestions is unchecked, hide past suggestions but always show current day
  if (
    !showLoggedTimeSuggestions.value &&
    isDateInPast(day.date) &&
    !isToday(day.date)
  ) {
    return [];
  }

  return getAllPlannedTimesForCell(user.id, day.date)
    .map((plannedTime) => {
      const plannedHours = getPlannedTimeHours(plannedTime);
      const loggedHours = getLoggedHoursForPlannedTime(
        user.id,
        day.date,
        plannedTime,
      );
      const remainingHours = Math.max(plannedHours - loggedHours, 0);

      return {
        id: `${plannedTime.id}-${day.date}`,
        user,
        day,
        plannedTime,
        hours: remainingHours,
      };
    })
    .filter((suggestion) => {
      return suggestion.hours > 0;
    });
};

const getVisibleRuns = (matches: (day: VisibleDay) => boolean) => {
  const runs: Array<{ startIndex: number; endIndex: number }> = [];
  let startIndex: number | null = null;

  visibleDays.value.forEach((day, index) => {
    if (matches(day)) {
      if (startIndex === null) {
        startIndex = index;
      }
      return;
    }

    if (startIndex !== null) {
      runs.push({ startIndex, endIndex: index - 1 });
      startIndex = null;
    }
  });

  if (startIndex !== null) {
    runs.push({ startIndex, endIndex: visibleDays.value.length - 1 });
  }

  return runs;
};

const assignMonthlyItemLanes = (items: MonthlyPlanningItem[]) => {
  const placedItems: MonthlyPlanningItem[] = [];

  return items
    .slice()
    .sort((a, b) => {
      return a.startIndex - b.startIndex || a.endIndex - b.endIndex;
    })
    .map((item) => {
      const occupiedLanes = new Set(
        placedItems
          .filter((placedItem) => {
            return (
              item.startIndex <= placedItem.endIndex &&
              placedItem.startIndex <= item.endIndex
            );
          })
          .map((placedItem) => placedItem.lane),
      );

      let lane = 0;

      while (occupiedLanes.has(lane)) {
        lane++;
      }

      const placedItem = { ...item, lane };
      placedItems.push(placedItem);

      return placedItem;
    });
};

const getMonthlyItemsForUser = (user: PlanningUser): MonthlyPlanningItem[] => {
  const items: MonthlyPlanningItem[] = [];

  if (showPlannedTime.value) {
    const plannedTimeGroups = new Map<string, PlanningPlannedTime[]>();

    for (const plannedTime of props.plannedTimes) {
      if (plannedTime.user?.id !== user.id) {
        continue;
      }

      const groupKey = plannedTime.periodAndRecurrenceId;

      const group = plannedTimeGroups.get(groupKey) ?? [];
      group.push(plannedTime);
      plannedTimeGroups.set(groupKey, group);
    }

    for (const [groupKey, group] of plannedTimeGroups) {
      const occurrenceDates = new Set(
        group
          .map((plannedTime) => plannedTime.startDate)
          .filter((date) => {
            return (
              shouldShowPlannedTime(date) &&
              getHolidaysForCell(user.id, date).length === 0
            );
          }),
      );
      const runs = getVisibleRuns((day) => occurrenceDates.has(day.date));

      runs.forEach((run, runIndex) => {
        const plannedTime =
          group.find(
            (item) =>
              item.startDate === visibleDays.value[run.startIndex]?.date,
          ) ?? group[0];

        items.push({
          key: `pt-${groupKey}-${runIndex}`,
          kind: "planned_time",
          startIndex: run.startIndex,
          endIndex: run.endIndex,
          lane: 0,
          plannedTime,
        });
      });
    }
  }

  const timeOffGroups = new Map<string, PlanningTimeOff[]>();

  for (const timeOff of props.timeOffs) {
    if (timeOff.user?.id !== user.id) {
      continue;
    }

    const groupKey = timeOff.periodAndRecurrenceId;

    const group = timeOffGroups.get(groupKey) ?? [];
    group.push(timeOff);
    timeOffGroups.set(groupKey, group);
  }

  for (const [groupKey, group] of timeOffGroups) {
    const occurrenceDates = new Set(group.map((timeOff) => timeOff.startDate));
    const runs = getVisibleRuns((day) => occurrenceDates.has(day.date));

    runs.forEach((run, runIndex) => {
      const timeOff =
        group.find(
          (item) => item.startDate === visibleDays.value[run.startIndex]?.date,
        ) ?? group[0];

      items.push({
        key: `to-${groupKey}-${runIndex}`,
        kind: "time_off",
        startIndex: run.startIndex,
        endIndex: run.endIndex,
        lane: 0,
        timeOff,
      });
    });
  }

  visibleDays.value.forEach((day, index) => {
    for (const timesheet of getTimesheetEntriesForCell(user.id, day.date)) {
      items.push({
        key: `ts-${timesheet.id}`,
        kind: "timesheet",
        startIndex: index,
        endIndex: index,
        lane: 0,
        timesheet,
      });
    }

    for (const suggestion of getTimesheetSuggestionsForCell(user, day)) {
      items.push({
        key: `suggestion-${suggestion.id}`,
        kind: "suggestion",
        startIndex: index,
        endIndex: index,
        lane: 0,
        suggestion,
      });
    }

    for (const holiday of getHolidaysForCell(user.id, day.date)) {
      items.push({
        key: `holiday-${holiday.id}-${day.date}`,
        kind: "holiday",
        startIndex: index,
        endIndex: index,
        lane: 0,
        holiday,
      });
    }

    if (isNonWorkingSlot(user, day)) {
      items.push({
        key: `nonworking-${user.id}-${day.date}`,
        kind: "nonworking",
        startIndex: index,
        endIndex: index,
        lane: 0,
        user,
        day,
      });
    }
  });

  return assignMonthlyItemLanes(items);
};

const getMonthlyItemStyle = (item: MonthlyPlanningItem) => {
  const dayCount = Math.max(visibleDays.value.length, 1);
  const left = (item.startIndex / dayCount) * 100;
  const width = ((item.endIndex - item.startIndex + 1) / dayCount) * 100;
  const style: Record<string, string> = {
    left: `${left}%`,
    width: `${width}%`,
    top: `${item.lane * MIN_ITEM_HEIGHT}px`,
    height: `${MIN_ITEM_HEIGHT}px`,
  };

  if (item.plannedTime) {
    style["--planned-time-project-color"] = getPlannedTimeBackground(
      item.plannedTime,
    );
  }

  if (item.timesheet) {
    style["--timesheet-project-color"] = getTimesheetProjectColor(
      item.timesheet,
    );
  }

  if (item.suggestion) {
    style["--timesheet-suggestion-color"] = getPlannedTimeBackground(
      item.suggestion.plannedTime,
    );
  }

  return style;
};

const getMonthlyItemClasses = (item: MonthlyPlanningItem) => {
  if (item.plannedTime) {
    return ["planned-time-block", getPlannedTimeStatusClass(item.plannedTime)];
  }

  if (item.timeOff) {
    return ["timeoff-block", getTimeOffClass(item.timeOff)];
  }

  if (item.holiday) {
    return ["holiday-block"];
  }

  if (item.timesheet) {
    return ["timesheet-block"];
  }

  if (item.suggestion) {
    return ["timesheet-suggestion-block"];
  }

  return ["nonworking-block"];
};

const getMonthlyItemLabel = (item: MonthlyPlanningItem) => {
  if (item.plannedTime) {
    return getPlannedTimeProjectName(item.plannedTime);
  }

  if (item.timeOff) {
    return getTimeOffLabel(item.timeOff);
  }

  if (item.holiday) {
    return item.holiday.name ?? "Holiday";
  }

  if (item.timesheet) {
    return getTimesheetProjectName(item.timesheet);
  }

  if (item.suggestion) {
    return getPlannedTimeProjectName(item.suggestion.plannedTime);
  }

  return "Non-working";
};

const getMonthlyItemTitle = (item: MonthlyPlanningItem) => {
  if (item.plannedTime) {
    return `${getPlannedTimeProjectName(item.plannedTime)} (${formatHoursAsTime(
      getPlannedTimeHours(item.plannedTime),
    )})`;
  }

  if (item.timeOff) {
    return `${getTimeOffLabel(item.timeOff)} (${formatHoursAsTime(
      getTimeOffHours(item.timeOff),
    )})`;
  }

  if (item.holiday) {
    return item.holiday.name ?? "Holiday";
  }

  if (item.timesheet) {
    return `${getTimesheetProjectName(item.timesheet)} (${formatMinutesAsTime(
      item.timesheet.duration,
    )})`;
  }

  if (item.suggestion) {
    return `${getPlannedTimeProjectName(
      item.suggestion.plannedTime,
    )} (${formatHoursAsTime(item.suggestion.hours)})`;
  }

  if (item.user && item.day) {
    return `Non-working (${formatHoursAsTime(
      getNonWorkingHours(item.user, item.day),
    )})`;
  }

  return "";
};

const handleMonthlyItemClick = (item: MonthlyPlanningItem) => {
  if (item.plannedTime) {
    handlePlannedTimeClick(item.plannedTime);
    return;
  }

  if (item.timeOff) {
    handleTimeOffClick(item.timeOff);
    return;
  }

  if (item.timesheet) {
    handleTimesheetClick(item.timesheet);
    return;
  }

  if (item.suggestion) {
    handleTimesheetSuggestionClick(item.suggestion);
  }
};

const getPlanningItemHeightNumber = (hours: number): number => {
  // Fixed visual scale, identical for every user/cell.
  // 15min to 1h stays one readable line; longer slots grow proportionally.
  // Example: 1h = 24px, 4h = 40px, 8h = 80px.
  const ITEM_HEIGHT_PER_HOUR = 10;
  const effectiveHours = Math.max(1, hours);

  return Math.max(MIN_ITEM_HEIGHT, effectiveHours * ITEM_HEIGHT_PER_HOUR);
};

const getPlanningItemHeight = (hours: number): string => {
  return `${getPlanningItemHeightNumber(hours)}px`;
};

const getTotalVisibleItemHeightForDay = (
  user: PlanningUser,
  day: VisibleDay,
): number => {
  const itemHeights: number[] = [];

  for (const pt of getPlannedTimesForCell(user.id, day.date)) {
    itemHeights.push(getPlanningItemHeightNumber(getPlannedTimeHours(pt)));
  }

  for (const to of getTimeOffsForCell(user.id, day.date)) {
    itemHeights.push(getPlanningItemHeightNumber(getTimeOffHours(to)));
  }

  const holidayCount = getHolidaysForCell(user.id, day.date).length;

  for (let i = 0; i < holidayCount; i++) {
    itemHeights.push(getPlanningItemHeightNumber(8));
  }

  for (const ts of getTimesheetEntriesForCell(user.id, day.date)) {
    itemHeights.push(getPlanningItemHeightNumber(getTimesheetHours(ts)));
  }

  for (const sug of getTimesheetSuggestionsForCell(user, day)) {
    itemHeights.push(getPlanningItemHeightNumber(sug.hours));
  }

  if (isNonWorkingSlot(user, day)) {
    itemHeights.push(
      getPlanningItemHeightNumber(getNonWorkingHours(user, day)),
    );
  }

  const itemCount = itemHeights.length;

  if (itemCount === 0) {
    return 0;
  }

  return (
    itemHeights.reduce((sum, height) => sum + height, 0) +
    Math.max(itemCount - 1, 0) * CELL_ITEM_GAP
  );
};

const getUserNaturalHeight = (user: PlanningUser): number => {
  if (monthView.value) {
    const monthlyItems = getMonthlyItemsForUser(user);
    const laneCount = monthlyItems.reduce((maxLane, item) => {
      return Math.max(maxLane, item.lane + 1);
    }, 0);

    return Math.max(
      props.minRowHeight,
      laneCount * MIN_ITEM_HEIGHT + CELL_CLICK_SPACE_HEIGHT,
    );
  }

  const maxVisibleContentHeight = Math.max(
    0,
    ...visibleDays.value.map((day) =>
      getTotalVisibleItemHeightForDay(user, day),
    ),
  );

  const contentBasedHeight =
    maxVisibleContentHeight + CELL_VERTICAL_PADDING + CELL_CLICK_SPACE_HEIGHT;

  // Row height only grows when the visible cells need more vertical space.
  // It does not depend on hidden past/future weeks, and it does not stretch to fill the panel.
  return Math.max(props.minRowHeight, contentBasedHeight);
};

const rowHeightsMap = computed(() => {
  const map = new Map<string, number>();

  users.value.forEach((user) => {
    map.set(user.id, getUserNaturalHeight(user));
  });

  return map;
});

const getUserRowHeight = (user: PlanningUser) => {
  return rowHeightsMap.value.get(user.id) ?? props.minRowHeight;
};

const isDateInPast = (date: string) => {
  const today = new Date().toISOString().split("T")[0];
  return date <= today;
};

const shouldShowLoggedTime = (date: string) => {
  const inPast = isDateInPast(date);

  if (showLoggedTime.value && !showPlannedTime.value) {
    return true;
  }

  if (showLoggedTime.value && showPlannedTime.value) {
    return inPast;
  }

  return false;
};

const shouldShowPlannedTime = (date: string) => {
  const inPast = isDateInPast(date);

  if (showPlannedTime.value && !showLoggedTime.value) {
    return true;
  }

  if (showLoggedTime.value && showPlannedTime.value) {
    return !inPast;
  }

  return false;
};

const getWorkingHoursForDay = (user: PlanningUser, day: VisibleDay) => {
  const workDayHours = user.workDayHours;

  if (!workDayHours?.length) {
    return 8;
  }

  const hoursForDay =
    workDayHours.length === 7
      ? workDayHours[day.dayOfWeek]
      : workDayHours[day.dayOfWeek - 1];

  return hoursForDay ?? 8;
};

const getNonWorkingHours = (user: PlanningUser, day: VisibleDay) => {
  return Math.max(8 - getWorkingHoursForDay(user, day), 0);
};

const isNonWorkingSlot = (user: PlanningUser, day: VisibleDay) => {
  return getNonWorkingHours(user, day) > 0;
};

const getPlannedTimeBackground = (plannedTime: PlanningPlannedTime) => {
  if (plannedTime.project?.color) {
    return getProjectColor(plannedTime.project);
  }

  const project = props.projects.find((item) => {
    return item.id === plannedTime.project?.id;
  });

  return getProjectColor(project);
};

const getTimesheetProjectColor = (timesheet: PlanningTimesheetEntry) => {
  if (timesheet.project?.color) {
    return getProjectColor(timesheet.project);
  }

  const project = props.projects.find((item) => {
    return item.id === timesheet.project?.id;
  });

  return getProjectColor(project);
};

const getPlannedTimeProjectName = (plannedTime: PlanningPlannedTime) => {
  return getProjectName(plannedTime.project);
};

const getTimesheetProjectName = (timesheet: PlanningTimesheetEntry) => {
  return getProjectName(timesheet.project);
};

const getPlannedTimeHours = (plannedTime: PlanningPlannedTime) => {
  if (plannedTime.adjustedHours != null) {
    return plannedTime.adjustedHours;
  }
  return (plannedTime.durationPerDay ?? 480) / 60;
};

const getTimeOffHours = (timeOff: PlanningTimeOff) => {
  return (timeOff.durationPerDay ?? 480) / 60;
};
const getTimesheetHours = (timesheet: PlanningTimesheetEntry) => {
  return Math.max(timesheet.duration / 60, 0.25);
};

const formatMinutesAsTime = (minutes: number) => {
  const safeMinutes = Math.max(Math.round(minutes), 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(remainingMinutes).padStart(
    2,
    "0",
  )}`;
};

const formatHoursAsTime = (hours: number) => {
  return formatMinutesAsTime(hours * 60);
};

const getHoursClass = (hours: number) => {
  if (hours >= 8) {
    return "hours-8";
  }

  if (hours >= 6) {
    return "hours-6";
  }

  if (hours >= 4) {
    return "hours-4";
  }

  if (hours >= 2) {
    return "hours-2";
  }

  return "hours-1";
};

const getPlannedTimeStatusClass = (plannedTime: PlanningPlannedTime) => {
  // Style depends only on tentative value
  // tentative = true -> dotted border (planned-time-tentative)
  // tentative = false -> default style (commercial status only affects icons)
  if (plannedTime.tentative === true) {
    return "planned-time-tentative";
  }

  // Non-tentative planned times use default style
  // Commercial status is only used for icons, not for base styling
  return "";
};

// Get tooltip for a specific date
const getPlannedTimeTooltip = (
  plannedTime: PlanningPlannedTime,
  date: string,
): string => {
  if (!plannedTime.commercialStatusByDate) {
    return "";
  }

  const statusForDate = plannedTime.commercialStatusByDate.find(
    (s) => s.date === date,
  );
  return statusForDate?.tooltip ?? "";
};

// Get status icon for planned time
const getPlannedTimeStatusIcon = (
  plannedTime: PlanningPlannedTime,
  date: string,
): {
  icon: string;
  color: string;
  style: "default" | "paid" | "confirmed";
} | null => {
  if (!plannedTime.commercialStatusByDate) {
    return null;
  }

  const statusForDate = plannedTime.commercialStatusByDate.find(
    (s) => s.date === date,
  );

  const commercialStatus = statusForDate?.status;
  const trackingMode = statusForDate?.trackingMode;

  if (!commercialStatus) {
    return null;
  }

  // Tentative keeps exactly the same icon/fill as the commercial status.
  // Only the icon color changes.
  const iconColor =
    plannedTime.tentative === true
      ? "var(--planned-time-project-color)"
      : "#ffffff";

  const isBudgetMode = trackingMode === "global_budget";
  const isInvoiceMode = trackingMode === "invoiced_amount";

  switch (commercialStatus) {
    case "secured":
      if (isInvoiceMode) {
        return {
          icon: "mdi:invoice-text-check",
          color: iconColor,
          style: "default",
        };
      }

      if (isBudgetMode) {
        return {
          icon: "f7:money-euro-circle-fill",
          color: iconColor,
          style: "default",
        };
      }

      return {
        icon: "material-symbols:check-circle",
        color: iconColor,
        style: "default",
      };

    case "committed":
      return {
        icon: "material-symbols:check-circle-outline",
        color: iconColor,
        style: "default",
      };

    case "pending":
      if (isInvoiceMode) {
        return {
          icon: "mdi:invoice-text-send-outline",
          color: iconColor,
          style: "default",
        };
      }

      if (isBudgetMode) {
        return {
          icon: "f7:money-euro-circle",
          color: iconColor,
          style: "default",
        };
      }

      return {
        icon: "material-symbols:send",
        color: iconColor,
        style: "default",
      };

    case "uncovered":
      return {
        icon: "material-symbols:warning-outline",
        color: iconColor,
        style: "default",
      };

    default:
      return null;
  }
};

const getTimeOffLabel = (timeOff: PlanningTimeOff) => {
  if (timeOff.type === "sick_leave") return "Sick Leave";
  if (timeOff.timeOffType === "sick_leave") return "Sick Leave";
  if (timeOff.type === "paid_leave") return "Paid Leave";
  if (timeOff.timeOffType === "paid_leave") return "Paid Leave";
  return timeOff.type ?? timeOff.timeOffType ?? "Time off";
};

const getTimeOffClass = (timeOff: PlanningTimeOff) => {
  const hoursClass = getHoursClass(getTimeOffHours(timeOff));
  if (timeOff.type === "sick_leave" || timeOff.timeOffType === "sick_leave") {
    return [hoursClass, "timeoff-block-sick-leave"];
  }
  return [hoursClass];
};

const handleCellClick = (
  user: PlanningUser,
  day: VisibleDay,
  event?: MouseEvent,
) => {
  if (props.readonly) {
    return;
  }

  if (event?.shiftKey) {
    // Time-off creation requires ownership or admin
    if (!canInteractWithUser(user.id)) {
      return;
    }
    emit("open-new-time-off", user, day);
  } else if (isDateInPast(day.date) && showLoggedTime.value) {
    // Timesheet creation requires ownership or admin
    if (!canInteractWithUser(user.id)) {
      return;
    }
    emit("open-new-timesheet", user, day);
  } else {
    // Planned time creation is allowed for any user
    emit("open-new-planned-time", user, day);
  }
};

const handlePlannedTimeClick = (plannedTime: PlanningPlannedTime) => {
  if (props.readonly) {
    return;
  }

  emit("open-edit-planned-time", plannedTime);
};

const handleTimeOffClick = (timeOff: PlanningTimeOff) => {
  if (props.readonly) {
    return;
  }

  if (!canInteractWithUser(timeOff.user.id)) {
    return;
  }

  emit("open-edit-time-off", timeOff);
};

const handleTimesheetClick = (timesheetEntry: PlanningTimesheetEntry) => {
  if (props.readonly) {
    return;
  }

  if (!canInteractWithUser(timesheetEntry.user.id)) {
    return;
  }

  emit("open-edit-timesheet", timesheetEntry);
};

const handleTimesheetSuggestionClick = (suggestion: TimesheetSuggestion) => {
  if (props.readonly) {
    return;
  }

  if (!canInteractWithUser(suggestion.user.id)) {
    return;
  }

  emit("open-new-timesheet-suggestion", suggestion);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["planning-container", {
      'planning-container--fill-height': __props.fillAvailableHeight,
      'planning-container--month-view': _unref(monthView),
    }])
  }, [
    (__props.showUserColumn)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", null, _toDisplayString(_unref(t)("planning.employees")), 1)
          ]),
          _createElementVNode("div", {
            ref_key: "usersListRef",
            ref: usersListRef,
            class: "users-list",
            onScroll: handleUsersListScroll
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(users), (user) => {
              return (_openBlock(), _createElementBlock("div", {
                key: user.id,
                class: "user-row",
                style: _normalizeStyle({ height: `${getUserRowHeight(user)}px` })
              }, [
                _createElementVNode("div", _hoisted_3, [
                  (_unref(hasUserAvatar)(user))
                    ? (_openBlock(), _createBlock(_unref(AoAvatar), {
                        key: 0,
                        "user-name": _unref(getUserName)(user),
                        "avatar-url": user.avatarUrl,
                        class: "user-avatar"
                      }, null, 8, ["user-name", "avatar-url"]))
                    : (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: "user-avatar",
                        style: _normalizeStyle({ backgroundColor: _unref(getUserColor)(user) })
                      }, _toDisplayString(_unref(getUserInitials)(user)), 5)),
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(getUserName)(user)), 1),
                    (user.role)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(user.role), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)("planning.capacity")), 1),
                    _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(getUserCapacity)(user)) + _toDisplayString(_unref(t)("planning.hoursPerWeek")), 1)
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("div", _hoisted_11, _toDisplayString(formatMinutesAsTime(getWeeklyLoggedMinutes(user))), 1)
                  ])
                ])
              ], 4))
            }), 128))
          ], 544)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_12, [
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("div", _hoisted_14, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weekGroups.value, (weekGroup) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `week-${weekGroup.startIndex}`,
              class: _normalizeClass(["week-header-cell", { 'week-start': weekGroup.startIndex > 0 }]),
              style: _normalizeStyle({
              flex: weekGroup.dayCount,
              minWidth: _unref(monthView) ? '0' : `${weekGroup.dayCount * 100}px`,
            })
            }, _toDisplayString(weekGroup.label), 7))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_15, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(visibleDays), (day, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: day.date,
              class: _normalizeClass(["day-header", {
              'is-today': __props.highlightToday && _unref(isToday)(day.date),
              'week-start': isWeekStart(index),
            }])
            }, [
              _createElementVNode("div", _hoisted_16, _toDisplayString(day.dayName), 1),
              _createElementVNode("div", _hoisted_17, _toDisplayString(day.dayDate), 1)
            ], 2))
          }), 128))
        ])
      ]),
      _createElementVNode("div", {
        ref_key: "scheduleBodyRef",
        ref: scheduleBodyRef,
        class: "schedule-body",
        onScroll: handleScheduleBodyScroll
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(users), (user) => {
          return (_openBlock(), _createElementBlock("div", {
            key: user.id,
            class: _normalizeClass(["schedule-row", { 'schedule-row--fill': __props.fillAvailableHeight }]),
            style: _normalizeStyle(
            __props.fillAvailableHeight
              ? undefined
              : { height: `${getUserRowHeight(user)}px` }
          )
          }, [
            (_unref(monthView))
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("div", _hoisted_18, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(visibleDays), (day, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: `${user.id}-${day.date}`,
                        class: _normalizeClass(["schedule-cell month-schedule-cell", {
                  'is-today': __props.highlightToday && _unref(isToday)(day.date),
                  'is-readonly': _unref(readonly),
                  'week-start': isWeekStart(index),
                  'missing-timesheet': isCellMissingTimesheet(user, day),
                }]),
                        onClick: ($event: any) => (handleCellClick(user, day, $event))
                      }, [
                        (getTotalTimesheetDurationForCell(user.id, day.date) > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_20, _toDisplayString(formatMinutesAsTime(
                      getTotalTimesheetDurationForCell(user.id, day.date),
                    )), 1))
                          : _createCommentVNode("", true)
                      ], 10, _hoisted_19))
                    }), 128))
                  ]),
                  _createElementVNode("div", _hoisted_21, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getMonthlyItemsForUser(user), (item) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: item.key,
                        class: _normalizeClass(["planning-item-block monthly-planning-item", [
                  ...getMonthlyItemClasses(item),
                  {
                    'is-resizing':
                      item.plannedTime &&
                      dragState.value?.plannedTime.id === item.plannedTime.id,
                    'is-resizable':
                      item.plannedTime &&
                      canResizePlannedTime(item.plannedTime),
                  },
                ]]),
                        style: _normalizeStyle(getMonthlyItemStyle(item)),
                        title: getMonthlyItemTitle(item),
                        onClick: _withModifiers(($event: any) => (handleMonthlyItemClick(item)), ["stop"])
                      }, [
                        (
                    item.plannedTime && canResizePlannedTime(item.plannedTime)
                  )
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              class: "resize-handle resize-handle-left",
                              onMousedown: _withModifiers(($event: any) => (
                    handleResizeStart($event, item.plannedTime, 'left')
                  ), ["stop"])
                            }, null, 40, _hoisted_23))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_24, [
                          _createElementVNode("span", _hoisted_25, _toDisplayString(getMonthlyItemLabel(item)), 1)
                        ]),
                        (
                    item.plannedTime && canResizePlannedTime(item.plannedTime)
                  )
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 1,
                              class: "resize-handle resize-handle-right",
                              onMousedown: _withModifiers(($event: any) => (
                    handleResizeStart($event, item.plannedTime, 'right')
                  ), ["stop"])
                            }, null, 40, _hoisted_26))
                          : _createCommentVNode("", true)
                      ], 14, _hoisted_22))
                    }), 128))
                  ])
                ], 64))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_unref(visibleDays), (day, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `${user.id}-${day.date}`,
                    class: _normalizeClass(["schedule-cell", {
                'is-today': __props.highlightToday && _unref(isToday)(day.date),
                'is-readonly': _unref(readonly),
                'has-both':
                  getPlannedTimesForCell(user.id, day.date).length > 0 &&
                  getTimeOffsForCell(user.id, day.date).length > 0,
                'week-start': isWeekStart(index),
                'missing-timesheet': isCellMissingTimesheet(user, day),
              }]),
                    onClick: ($event: any) => (handleCellClick(user, day, $event))
                  }, [
                    _createElementVNode("div", _hoisted_28, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getTimeOffsForCell(user.id, day.date), (timeOff) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `to-${timeOff.id}`,
                          class: _normalizeClass(["timeoff-block planning-item-block", getTimeOffClass(timeOff)]),
                          style: _normalizeStyle({
                    height: getPlanningItemHeight(getTimeOffHours(timeOff)),
                  }),
                          title: `${getTimeOffLabel(timeOff)} (${formatHoursAsTime(
                    getTimeOffHours(timeOff),
                  )})`,
                          onClick: _withModifiers(($event: any) => (handleTimeOffClick(timeOff)), ["stop"])
                        }, [
                          _createElementVNode("div", _hoisted_30, [
                            _createElementVNode("span", _hoisted_31, _toDisplayString(getTimeOffLabel(timeOff)), 1),
                            _createElementVNode("span", _hoisted_32, _toDisplayString(formatHoursAsTime(getTimeOffHours(timeOff))), 1)
                          ])
                        ], 14, _hoisted_29))
                      }), 128)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getPlannedTimesForCell(
                    user.id,
                    day.date,
                  ), (plannedTime) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: plannedTime.id,
                          class: _normalizeClass(["planned-time-block planning-item-block", [
                    getPlannedTimeStatusClass(plannedTime),
                    getHoursClass(getPlannedTimeHours(plannedTime)),
                    {
                      'is-resizing':
                        dragState.value?.plannedTime.id === plannedTime.id,
                      'is-start-day': isPlannedTimeStartDay(
                        day.date,
                        plannedTime,
                      ),
                      'is-end-day': isPlannedTimeEndDay(day.date, plannedTime),
                      'is-resizable': canResizePlannedTime(plannedTime),
                    },
                  ]]),
                          style: _normalizeStyle({
                    '--planned-time-project-color':
                      getPlannedTimeBackground(plannedTime),
                    height: getPlanningItemHeight(
                      getPlannedTimeHours(plannedTime),
                    ),
                  }),
                          title: 
                    `${getPlannedTimeProjectName(
                      plannedTime,
                    )} (${formatHoursAsTime(
                      getPlannedTimeHours(plannedTime),
                    )})` +
                    (getPlannedTimeTooltip(plannedTime, day.date)
                      ? '\n' + getPlannedTimeTooltip(plannedTime, day.date)
                      : '') +
                    (plannedTime.notes ? '\n\n💬 ' + plannedTime.notes : '')
                  ,
                          onClick: _withModifiers(($event: any) => (handlePlannedTimeClick(plannedTime)), ["stop"])
                        }, [
                          (
                      canResizePlannedTime(plannedTime) &&
                      isPlannedTimeStartDay(day.date, plannedTime)
                    )
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "resize-handle resize-handle-left",
                                onMousedown: _withModifiers(($event: any) => (
                      handleResizeStart($event, plannedTime, 'left')
                    ), ["stop"])
                              }, null, 40, _hoisted_34))
                            : _createCommentVNode("", true),
                          (getPlannedTimeStatusIcon(plannedTime, day.date))
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 1,
                                class: _normalizeClass(["status-icon", {
                      'status-icon--paid':
                        getPlannedTimeStatusIcon(plannedTime, day.date)
                          ?.style === 'paid',
                      'status-icon--confirmed':
                        getPlannedTimeStatusIcon(plannedTime, day.date)
                          ?.style === 'confirmed',
                    }])
                              }, [
                                _createVNode(_unref(Icon), {
                                  icon: 
                        getPlannedTimeStatusIcon(plannedTime, day.date)!.icon
                      ,
                                  style: _normalizeStyle({
                        color: getPlannedTimeStatusIcon(plannedTime, day.date)!
                          .color,
                      }),
                                  width: 14,
                                  height: 14
                                }, null, 8, ["icon", "style"])
                              ], 2))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", {
                            class: _normalizeClass(["planning-item-content", {
                      'has-status-icon': getPlannedTimeStatusIcon(
                        plannedTime,
                        day.date,
                      ),
                    }])
                          }, [
                            (plannedTime.notes)
                              ? (_openBlock(), _createBlock(_unref(Icon), {
                                  key: 0,
                                  icon: "mdi:comment-text-outline",
                                  class: "notes-inline-icon",
                                  width: 14,
                                  height: 14
                                }))
                              : _createCommentVNode("", true),
                            _createElementVNode("span", _hoisted_35, _toDisplayString(getPlannedTimeProjectName(plannedTime)), 1),
                            _createElementVNode("span", _hoisted_36, _toDisplayString(formatHoursAsTime(getPlannedTimeHours(plannedTime))), 1)
                          ], 2),
                          (
                      canResizePlannedTime(plannedTime) &&
                      isPlannedTimeEndDay(day.date, plannedTime)
                    )
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 2,
                                class: "resize-handle resize-handle-right",
                                onMousedown: _withModifiers(($event: any) => (
                      handleResizeStart($event, plannedTime, 'right')
                    ), ["stop"])
                              }, null, 40, _hoisted_37))
                            : _createCommentVNode("", true)
                        ], 14, _hoisted_33))
                      }), 128)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getHolidaysForCell(user.id, day.date), (holiday) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `holiday-${holiday.id}`,
                          class: "holiday-block planning-item-block hours-8",
                          style: _normalizeStyle({ height: getPlanningItemHeight(8) }),
                          title: holiday.name ?? _unref(t)('planning.holiday')
                        }, [
                          _createElementVNode("div", _hoisted_39, [
                            _createElementVNode("span", _hoisted_40, _toDisplayString(holiday.name ?? _unref(t)("planning.holiday")), 1)
                          ])
                        ], 12, _hoisted_38))
                      }), 128)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getTimesheetEntriesForCell(
                    user.id,
                    day.date,
                  ), (timesheet) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `ts-${timesheet.id}`,
                          class: _normalizeClass(["timesheet-block planning-item-block", getHoursClass(getTimesheetHours(timesheet))]),
                          style: _normalizeStyle({
                    '--timesheet-project-color':
                      getTimesheetProjectColor(timesheet),
                    height: getPlanningItemHeight(getTimesheetHours(timesheet)),
                  }),
                          title: 
                    `${getTimesheetProjectName(
                      timesheet,
                    )} (${formatMinutesAsTime(timesheet.duration)})` +
                    (timesheet.notes ? '\n💬 ' + timesheet.notes : '')
                  ,
                          onClick: _withModifiers(($event: any) => (handleTimesheetClick(timesheet)), ["stop"])
                        }, [
                          _createElementVNode("div", _hoisted_42, [
                            (timesheet.notes)
                              ? (_openBlock(), _createBlock(_unref(Icon), {
                                  key: 0,
                                  icon: "mdi:comment-text-outline",
                                  class: "notes-inline-icon",
                                  width: 14,
                                  height: 14
                                }))
                              : _createCommentVNode("", true),
                            _createElementVNode("span", _hoisted_43, _toDisplayString(getTimesheetProjectName(timesheet)), 1),
                            _createElementVNode("span", _hoisted_44, _toDisplayString(formatMinutesAsTime(timesheet.duration)), 1)
                          ])
                        ], 14, _hoisted_41))
                      }), 128)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getTimesheetSuggestionsForCell(
                    user,
                    day,
                  ), (suggestion) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `suggestion-${suggestion.id}`,
                          class: _normalizeClass(["timesheet-suggestion-block planning-item-block", getHoursClass(suggestion.hours)]),
                          style: _normalizeStyle({
                    '--timesheet-suggestion-color': getPlannedTimeBackground(
                      suggestion.plannedTime,
                    ),
                    height: getPlanningItemHeight(suggestion.hours),
                  }),
                          title: `${getPlannedTimeProjectName(
                    suggestion.plannedTime,
                  )} (${formatHoursAsTime(suggestion.hours)})`,
                          onClick: _withModifiers(($event: any) => (handleTimesheetSuggestionClick(suggestion)), ["stop"])
                        }, [
                          _createElementVNode("div", _hoisted_46, [
                            _createElementVNode("span", _hoisted_47, _toDisplayString(getPlannedTimeProjectName(suggestion.plannedTime)), 1),
                            _createElementVNode("span", _hoisted_48, _toDisplayString(formatHoursAsTime(suggestion.hours)), 1)
                          ])
                        ], 14, _hoisted_45))
                      }), 128)),
                      (isNonWorkingSlot(user, day))
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: _normalizeClass(["nonworking-block planning-item-block", getHoursClass(getNonWorkingHours(user, day))]),
                            style: _normalizeStyle({
                    height: getPlanningItemHeight(
                      getNonWorkingHours(user, day),
                    ),
                  }),
                            title: `${_unref(t)('planning.nonWorking')} (${formatHoursAsTime(
                    getNonWorkingHours(user, day),
                  )})`
                          }, [
                            _createElementVNode("div", _hoisted_50, [
                              _createElementVNode("span", _hoisted_51, _toDisplayString(_unref(t)("planning.nonWorking")), 1),
                              _createElementVNode("span", _hoisted_52, _toDisplayString(formatHoursAsTime(getNonWorkingHours(user, day))), 1)
                            ])
                          ], 14, _hoisted_49))
                        : _createCommentVNode("", true),
                      _cache[0] || (_cache[0] = _createElementVNode("div", { class: "cell-click-space" }, null, -1))
                    ]),
                    (getTotalTimesheetDurationForCell(user.id, day.date) > 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_53, _toDisplayString(formatMinutesAsTime(
                    getTotalTimesheetDurationForCell(user.id, day.date),
                  )), 1))
                      : _createCommentVNode("", true)
                  ], 10, _hoisted_27))
                }), 128))
          ], 6))
        }), 128))
      ], 544)
    ])
  ], 2))
}
}

})